import { i18n } from '@cccom/shared/i18n';
import { Text } from '@chakra-ui/react';
import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import Select, {
  ActionMeta,
  components as defaultComponents,
  GroupBase,
  OnChangeValue,
  Props as SelectProps,
} from 'react-select';

import {
  getSelectClassNames,
  isSelectOption,
  SelectOption,
} from '../select.utils';

type ControlledSelectProps<
  FormValues extends FieldValues = FieldValues,
  Option = SelectOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = { placeholderLabel?: string; hasErrors?: boolean } & Omit<
  SelectProps<Option, IsMulti, Group>,
  'name' | 'defaultValue'
> &
  UseControllerProps<FormValues>;

/**
 * Reusable react-select form component
 *
 * @param props - The combined props of the react-select component and the useController hook
 */
export function ControlledSelect<
  FormValues extends FieldValues = FieldValues,
  Option = SelectOption,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  name,
  options,
  control,
  shouldUnregister,
  placeholderLabel = i18n.t('common.placeholder.type_to_search'),
  hasErrors = false,
  ...selectProps
}: ControlledSelectProps<FormValues, Option, IsMulti, Group>) {
  const {
    field, // contains: onChange, onBlur, value, name, ref
  } = useController<FormValues>({
    name,
    control,
    shouldUnregister,
  });

  const defaultSelectedValue = field.value;
  const { isMulti } = selectProps;

  const selectedValue = (() => {
    if (defaultSelectedValue && options) {
      const getOption = (value: string) =>
        (options.find(
          (_option: any) => isSelectOption(_option) && _option.value === value
        ) as Option) || ({ label: value, value } as Option);

      const values = Array.isArray(defaultSelectedValue)
        ? defaultSelectedValue
        : [defaultSelectedValue];

      return isMulti
        ? values.map((value) => getOption(value))
        : getOption(values[0]);
    }
    return null;
  })();

  const onChange = (
    option: OnChangeValue<Option, IsMulti>,
    action: ActionMeta<Option>
  ) => {
    const isMultiSelectOption =
      selectProps.isMulti && Array.isArray(option) && isSelectOption(option[0]);

    if (isMultiSelectOption) {
      field.onChange(option.map((v: SelectOption) => v.value));
    } else if (isSelectOption(option)) {
      field.onChange(option.value);
    } else {
      field.onChange(option);
    }

    // required for custom onChange callback
    selectProps?.onChange?.(option, action);
  };

  return (
    <Select<Option, IsMulti, Group>
      options={options}
      placeholder={
        <Text fontSize="sm" fontFamily="Lexend">
          {placeholderLabel}
        </Text>
      }
      {...selectProps}
      {...field}
      aria-label={name}
      value={selectedValue}
      onChange={onChange}
      classNames={getSelectClassNames(hasErrors)}
      components={{
        ...defaultComponents,
      }}
    />
  );
}
